'use client';

import { motion } from 'framer-motion';
import { Award, Compass, HeartHandshake, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { fadeUp, staggerContainer } from '@/lib/motion';
import { Section } from '@/components/layout/Section';

interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
}

const DEFAULT_FEATURES: Feature[] = [
  {
    icon: ShieldCheck,
    title: 'Annonces vérifiées',
    description: 'Chaque bien est contrôlé par notre équipe : titres, mandats, photos et localisation.',
  },
  {
    icon: Users,
    title: 'Conseillers dédiés',
    description: 'Un interlocuteur unique, expert de votre ville, du premier contact à la signature.',
  },
  {
    icon: Compass,
    title: 'Une connaissance fine du Maroc',
    description: 'Quartiers historiques, projets neufs, riads de caractère — nous connaissons chaque parcelle.',
  },
  {
    icon: Award,
    title: 'Sélection éditoriale',
    description: 'Les “Coups de cœur” réunissent les biens les plus singuliers du moment.',
  },
  {
    icon: HeartHandshake,
    title: 'Accompagnement global',
    description: 'Notaire, financement, rénovation, gestion locative : tout sous le même toit.',
  },
  {
    icon: Sparkles,
    title: 'Une expérience pensée',
    description: 'Interface élégante, mobile-first, accessible — la recherche devient un plaisir.',
  },
];

interface WhyChooseUsProps {
  features?: Feature[];
  eyebrow?: string;
  heading?: string;
  description?: string;
}

export function WhyChooseUs({
  features = DEFAULT_FEATURES,
  eyebrow = 'Notre signature',
  heading = 'Pourquoi nous choisir',
  description = 'Une plateforme conçue par et pour des connaisseurs de l’immobilier marocain.',
}: WhyChooseUsProps) {
  return (
    <Section
      eyebrow={eyebrow}
      heading={heading}
      description={description}
      tone="cream"
      spacing="lg"
    >
      <motion.div
        variants={staggerContainer(0.06, 0.05)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div
              key={i}
              variants={fadeUp}
              className="group rounded-2xl bg-surface border border-border-subtle p-7 transition-all duration-400 ease-premium hover:-translate-y-1 hover:shadow-card-hover"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground mb-5">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-h3 text-ink mb-2 leading-tight">{f.title}</h3>
              <p className="text-sm text-ink-secondary leading-relaxed">{f.description}</p>
            </motion.div>
          );
        })}
      </motion.div>
    </Section>
  );
}
